import { useState, useCallback, useMemo, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import {
    UPDATE_SURVEY_ITEM_PROPOSED_PRICE,
    CREATE_PCR_FROM_SURVEY_ITEM,
} from '../apollo/mutations/survey';
import { GET_SURVEY } from '../apollo/queries/survey';
import {
    showErrorNotification,
    showSuccessNotification,
    showWarningNotification,
    showLoadingNotification,
    updateSuccessNotification,
    updateFailedNotification,
} from '../notifications';
import {
    SurveyItemRow,
    DivisionalManagerItemState,
} from '../pages/price-surveys/types';
import {
    allCompetitorPricesAreNA,
    getItemsMissingNaReason,
} from '../utility/surveyUtils';

export type DivisionalManagerItemStateMap = Map<
    string,
    DivisionalManagerItemState
>;

const SAVE_PROGRESS_NOTIFICATION_ID = 'divisional-manager-survey-save-progress';

export function useDivisionalManagerSurveyProgress(
    surveyId: string,
    surveyItems: SurveyItemRow[],
    onSuccess?: () => void | Promise<void>,
) {
    const [itemStates, setItemStates] = useState<DivisionalManagerItemStateMap>(
        () => {
            const map = new Map<string, DivisionalManagerItemState>();
            surveyItems.forEach(item => {
                if (item.priceChangeRequestId) return;

                map.set(item.id, {
                    approvedForPriceChange:
                        item.approvedForPriceChange ?? false,
                    isDeclinedForPriceChange:
                        item.isDeclinedForPriceChange ?? false,
                    proposedSellingPrice:
                        item.proposedSellingPrice ?? undefined,
                    declinedReason: item.declinedReason ?? null,
                });
            });
            return map;
        },
    );

    const [submitErrorIds, setSubmitErrorIds] = useState<string[]>([]);
    const [submitErrorMessages, setSubmitErrorMessages] = useState<
        Record<string, string>
    >({});

    useEffect(() => {
        setItemStates(prev => {
            const newMap = new Map<string, DivisionalManagerItemState>();
            surveyItems.forEach(item => {
                if (item.priceChangeRequestId) return;

                const existing = prev.get(item.id);
                if (existing) {
                    newMap.set(item.id, existing);
                } else {
                    newMap.set(item.id, {
                        approvedForPriceChange:
                            item.approvedForPriceChange ?? false,
                        isDeclinedForPriceChange:
                            item.isDeclinedForPriceChange ?? false,
                        proposedSellingPrice:
                            item.proposedSellingPrice ?? undefined,
                        declinedReason: item.declinedReason ?? null,
                    });
                }
            });
            return newMap;
        });
    }, [surveyItems]);

    const [bulkSaveProgress, { loading: isSaving }] = useMutation(
        UPDATE_SURVEY_ITEM_PROPOSED_PRICE,
        {
            refetchQueries: [
                { query: GET_SURVEY, variables: { id: surveyId } },
            ],
            awaitRefetchQueries: true,
        },
    );

    const [submitDivisionalManagerSurveyMutation, { loading: isSubmitting }] =
        useMutation(CREATE_PCR_FROM_SURVEY_ITEM, {
            refetchQueries: [
                { query: GET_SURVEY, variables: { id: surveyId } },
            ],
            awaitRefetchQueries: true,
        });

    const getItemState = useCallback(
        (itemId: string): DivisionalManagerItemState | undefined => {
            return itemStates.get(itemId);
        },
        [itemStates],
    );

    const updateItemState = useCallback(
        (itemId: string, update: Partial<DivisionalManagerItemState>) => {
            setSubmitErrorIds(prev => prev.filter(id => id !== itemId));
            setSubmitErrorMessages(prev => {
                const next = { ...prev };
                delete next[itemId];
                return next;
            });
            setItemStates(prev => {
                const newMap = new Map(prev);
                const existing = newMap.get(itemId);
                if (existing) {
                    const newState = { ...existing, ...update };

                    if (update.approvedForPriceChange === true) {
                        newState.isDeclinedForPriceChange = false;
                        newState.declinedReason = null;
                    }
                    if (update.isDeclinedForPriceChange === true) {
                        newState.approvedForPriceChange = false;
                    }

                    newMap.set(itemId, newState);
                }
                return newMap;
            });
        },
        [],
    );

    const markForPcr = useCallback(
        (itemId: string, proposedPrice?: number) => {
            updateItemState(itemId, {
                approvedForPriceChange: true,
                isDeclinedForPriceChange: false,
                declinedReason: null,
                ...(proposedPrice !== undefined && {
                    proposedSellingPrice: proposedPrice,
                }),
            });
        },
        [updateItemState],
    );

    const markAsNA = useCallback(
        (itemId: string, reason: string | null) => {
            updateItemState(itemId, {
                isDeclinedForPriceChange: true,
                approvedForPriceChange: false,
                declinedReason: reason,
                proposedSellingPrice: null,
            });
        },
        [updateItemState],
    );

    const setProposedPrice = useCallback(
        (itemId: string, price: number | null) => {
            updateItemState(itemId, { proposedSellingPrice: price });
        },
        [updateItemState],
    );

    const setNaReason = useCallback(
        (itemId: string, reason: string | null) => {
            updateItemState(itemId, { declinedReason: reason });
        },
        [updateItemState],
    );

    const clearItemState = useCallback((itemId: string) => {
        setSubmitErrorIds(prev => prev.filter(id => id !== itemId));
        setSubmitErrorMessages(prev => {
            const next = { ...prev };
            delete next[itemId];
            return next;
        });
        setItemStates(prev => {
            const newMap = new Map(prev);
            const existing = newMap.get(itemId);
            if (existing) {
                newMap.set(itemId, {
                    approvedForPriceChange: false,
                    isDeclinedForPriceChange: false,
                    proposedSellingPrice: null,
                    declinedReason: null,
                });
            }
            return newMap;
        });
    }, []);

    const hasChanges = useMemo(() => {
        return surveyItems.some(item => {
            if (item.priceChangeRequestId) return false;

            const state = itemStates.get(item.id);
            if (!state) return false;

            const original = {
                approvedForPriceChange: item.approvedForPriceChange ?? false,
                isDeclinedForPriceChange:
                    item.isDeclinedForPriceChange ?? false,
                proposedSellingPrice: item.proposedSellingPrice ?? null,
                declinedReason: item.declinedReason ?? null,
            };

            return (
                state.approvedForPriceChange !==
                    original.approvedForPriceChange ||
                state.isDeclinedForPriceChange !==
                    original.isDeclinedForPriceChange ||
                (state.proposedSellingPrice ?? null) !==
                    original.proposedSellingPrice ||
                state.declinedReason !== original.declinedReason
            );
        });
    }, [surveyItems, itemStates]);

    const changedCount = useMemo(() => {
        return surveyItems.filter(item => {
            if (item.priceChangeRequestId) return false;

            const state = itemStates.get(item.id);
            if (!state) return false;

            const original = {
                approvedForPriceChange: item.approvedForPriceChange ?? false,
                isDeclinedForPriceChange:
                    item.isDeclinedForPriceChange ?? false,
                proposedSellingPrice: item.proposedSellingPrice ?? null,
                declinedReason: item.declinedReason ?? null,
            };

            return (
                state.approvedForPriceChange !==
                    original.approvedForPriceChange ||
                state.isDeclinedForPriceChange !==
                    original.isDeclinedForPriceChange ||
                (state.proposedSellingPrice ?? null) !==
                    original.proposedSellingPrice ||
                state.declinedReason !== original.declinedReason
            );
        }).length;
    }, [surveyItems, itemStates]);

    const actionableItems = useMemo(() => {
        return surveyItems.filter(
            item =>
                !item.priceChangeRequestId && !allCompetitorPricesAreNA(item),
        ).length;
    }, [surveyItems]);

    const actionedItems = useMemo(() => {
        return surveyItems.filter(item => {
            if (item.priceChangeRequestId || allCompetitorPricesAreNA(item))
                return false;
            const state = itemStates.get(item.id);
            return Boolean(
                state &&
                    (state.approvedForPriceChange ||
                        state.isDeclinedForPriceChange),
            );
        }).length;
    }, [surveyItems, itemStates]);

    const allItemsActioned =
        actionableItems === 0 || actionedItems === actionableItems;
    const completionPercent =
        actionableItems > 0
            ? Math.round((actionedItems / actionableItems) * 100)
            : 0;

    const saveProgress = useCallback(async (): Promise<boolean> => {
        if (!hasChanges) {
            showSuccessNotification('No changes to save');
            return true;
        }

        const naWithoutReason = getItemsMissingNaReason(
            surveyItems,
            itemStates,
        );
        if (naWithoutReason.ids.length > 0) {
            const errorMap: Record<string, string> = {};
            naWithoutReason.ids.forEach(id => {
                errorMap[id] = 'Please select a decline reason';
            });
            setSubmitErrorIds(naWithoutReason.ids);
            setSubmitErrorMessages(errorMap);
            showErrorNotification(
                `Please select a reason for declined for: ${naWithoutReason.labels
                    .slice(0, 3)
                    .join(', ')}${
                    naWithoutReason.labels.length > 3 ? '...' : ''
                }`,
            );
            return false;
        }

        const items: {
            surveyItemId: string;
            proposedSellingPrice?: number | null;
            approvedForPriceChange?: boolean;
            isDeclinedForPriceChange?: boolean;
            declinedReason?: string | null;
        }[] = [];

        surveyItems.forEach(item => {
            if (item.priceChangeRequestId) return;

            const state = itemStates.get(item.id);
            if (!state) return;

            const original = {
                approvedForPriceChange: item.approvedForPriceChange ?? false,
                isDeclinedForPriceChange:
                    item.isDeclinedForPriceChange ?? false,
                proposedSellingPrice: item.proposedSellingPrice ?? null,
                declinedReason: item.declinedReason ?? null,
            };

            const hasItemChanged =
                state.approvedForPriceChange !==
                    original.approvedForPriceChange ||
                state.isDeclinedForPriceChange !==
                    original.isDeclinedForPriceChange ||
                (state.proposedSellingPrice ?? null) !==
                    original.proposedSellingPrice ||
                state.declinedReason !== original.declinedReason;

            if (hasItemChanged) {
                items.push({
                    surveyItemId: item.id,
                    proposedSellingPrice: state.proposedSellingPrice,
                    approvedForPriceChange: state.approvedForPriceChange,
                    isDeclinedForPriceChange: state.isDeclinedForPriceChange,
                    declinedReason: state.declinedReason,
                });
            }
        });

        if (items.length === 0) {
            await onSuccess?.();
            return true;
        }

        const validationErrors: { surveyItemId: string; message: string }[] =
            [];

        items.forEach(payload => {
            if (
                payload.isDeclinedForPriceChange &&
                !payload.declinedReason?.trim()
            ) {
                validationErrors.push({
                    surveyItemId: payload.surveyItemId,
                    message: 'A reason is required for declined items.',
                });
            }
        });

        if (validationErrors.length > 0) {
            const errorMap: Record<string, string> = {};
            validationErrors.forEach(({ surveyItemId, message }) => {
                errorMap[surveyItemId] = message;
            });
            setSubmitErrorIds(validationErrors.map(e => e.surveyItemId));
            setSubmitErrorMessages(errorMap);
            showErrorNotification(
                `${validationErrors.length} item${
                    validationErrors.length === 1 ? '' : 's'
                } failed validation. Check highlighted cells.`,
            );
            return false;
        }

        showLoadingNotification(
            SAVE_PROGRESS_NOTIFICATION_ID,
            'Saving Progress',
            `Updating ${items.length} item${items.length === 1 ? '' : 's'}...`,
        );

        try {
            const result = await bulkSaveProgress({
                variables: {
                    input: {
                        surveyId,
                        items,
                    },
                },
            });

            if (result.data?.updateSurveyItemProposedPrice?.success) {
                setSubmitErrorIds([]);
                setSubmitErrorMessages({});
                updateSuccessNotification(
                    SAVE_PROGRESS_NOTIFICATION_ID,
                    'Progress Saved',
                    `Successfully updated ${items.length} item${
                        items.length === 1 ? '' : 's'
                    }`,
                );
                await onSuccess?.();
                return true;
            }
            const errors = result.data?.updateSurveyItemProposedPrice?.errors;
            if (errors?.length) {
                const ids = errors.map(
                    (e: { surveyItemId: string; message: string }) =>
                        e.surveyItemId,
                );
                const messageMap: Record<string, string> = {};
                errors.forEach(
                    (e: { surveyItemId: string; message: string }) => {
                        messageMap[e.surveyItemId] = e.message;
                    },
                );
                setSubmitErrorIds(ids);
                setSubmitErrorMessages(messageMap);
                updateFailedNotification(
                    SAVE_PROGRESS_NOTIFICATION_ID,
                    'Save Failed',
                    `${errors.length} item(s) could not be saved. Check the highlighted cells.`,
                );
                return false;
            }
            setSubmitErrorIds([]);
            setSubmitErrorMessages({});
            updateFailedNotification(
                SAVE_PROGRESS_NOTIFICATION_ID,
                'Save Failed',
                'An error occurred while saving',
            );
            return false;
        } catch (error) {
            setSubmitErrorIds([]);
            setSubmitErrorMessages({});
            updateFailedNotification(
                SAVE_PROGRESS_NOTIFICATION_ID,
                'Save Failed',
                error instanceof Error
                    ? error.message
                    : 'Failed to save progress',
            );
            return false;
        }
    }, [
        hasChanges,
        itemStates,
        surveyItems,
        surveyId,
        bulkSaveProgress,
        onSuccess,
    ]);

    const submitSurvey = useCallback(async () => {
        const actionableCount = surveyItems.filter(
            item =>
                !item.priceChangeRequestId && !allCompetitorPricesAreNA(item),
        ).length;
        if (actionableCount === 0) {
            showErrorNotification(
                'All items are already processed. Nothing to submit.',
            );
            return;
        }

        const invalidItems: string[] = [];
        surveyItems.forEach(item => {
            if (item.priceChangeRequestId || allCompetitorPricesAreNA(item))
                return;
            const state = itemStates.get(item.id);
            if (!state?.approvedForPriceChange) return;
            const proposedPrice =
                state.proposedSellingPrice ?? item.suggestedPrice ?? null;
            if (
                proposedPrice === undefined ||
                proposedPrice === null ||
                proposedPrice <= 0
            ) {
                invalidItems.push(item.sku?.replace(/^0+/, '') ?? item.id);
            }
        });
        if (invalidItems.length > 0) {
            showErrorNotification(
                `Please enter valid proposed prices for: ${invalidItems
                    .slice(0, 5)
                    .join(', ')}${invalidItems.length > 5 ? '...' : ''}`,
            );
            return;
        }

        const naWithoutReason = getItemsMissingNaReason(
            surveyItems,
            itemStates,
        );
        if (naWithoutReason.ids.length > 0) {
            const errorMap: Record<string, string> = {};
            naWithoutReason.ids.forEach(id => {
                errorMap[id] = 'Please select a decline reason';
            });
            setSubmitErrorIds(naWithoutReason.ids);
            setSubmitErrorMessages(errorMap);
            showErrorNotification(
                `Please select a reason for declined for: ${naWithoutReason.labels
                    .slice(0, 3)
                    .join(', ')}${
                    naWithoutReason.labels.length > 3 ? '...' : ''
                }`,
            );
            return;
        }

        if (hasChanges) {
            const saved = await saveProgress();
            if (!saved) return;
        }

        setSubmitErrorIds([]);

        try {
            const result = await submitDivisionalManagerSurveyMutation({
                variables: {
                    input: { surveyId },
                },
            });

            const submitResult = result.data?.createPcrFromSurveyItem;
            if (submitResult?.success) {
                setSubmitErrorIds([]);
                setSubmitErrorMessages({});
                const { pcrsCreated, itemsMarkedNA, createdPcrs } =
                    submitResult;
                const autoDeclined =
                    createdPcrs?.filter(pcr => pcr.isAutoDeclined) ?? [];
                showSuccessNotification(
                    `Survey submitted! Created ${pcrsCreated} PCR(s), ${itemsMarkedNA} item(s) marked declined`,
                );
                if (autoDeclined.length > 0) {
                    const visibleSkuLimit = 5;
                    const skus = autoDeclined.map(
                        pcr => pcr.sku?.replace(/^0+/, '') ?? pcr.sku,
                    );
                    const visibleSkus = skus.slice(0, visibleSkuLimit);
                    const remainingCount = skus.length - visibleSkus.length;
                    const skuList =
                        remainingCount > 0
                            ? `${visibleSkus.join(
                                  ', ',
                              )} and ${remainingCount} more`
                            : visibleSkus.join(', ');
                    showWarningNotification(
                        `${autoDeclined.length} PCR(s) were automatically declined due to restricted SKU/store rules: ${skuList}`,
                    );
                }
                try {
                    await onSuccess?.();
                } catch (callbackError) {
                    showErrorNotification(
                        callbackError instanceof Error
                            ? callbackError.message
                            : 'Survey submitted, but failed to refresh data',
                    );
                }
            } else if (submitResult?.partialSuccess) {
                const errors = submitResult.errors ?? [];
                const ids = errors.map(e => e.surveyItemId);
                const messageMap: Record<string, string> = {};
                errors.forEach(e => {
                    messageMap[e.surveyItemId] = e.message;
                });
                setSubmitErrorIds(ids);
                setSubmitErrorMessages(messageMap);
                showWarningNotification(
                    `Partial submission: ${submitResult.pcrsCreated} PCR(s) created, ${errors.length} item(s) failed. Retry for remaining.`,
                );
                try {
                    await onSuccess?.();
                } catch (callbackError) {
                    showErrorNotification(
                        callbackError instanceof Error
                            ? callbackError.message
                            : 'Partial submission succeeded, but failed to refresh data',
                    );
                }
            } else {
                const errors = submitResult?.errors;
                if (errors?.length) {
                    const ids = errors.map(e => e.surveyItemId);
                    const messageMap: Record<string, string> = {};
                    errors.forEach(e => {
                        messageMap[e.surveyItemId] = e.message;
                    });
                    setSubmitErrorIds(ids);
                    setSubmitErrorMessages(messageMap);
                    showErrorNotification(
                        'Some items need attention. Check the highlighted cells.',
                    );
                }
            }
        } catch (error) {
            setSubmitErrorIds([]);
            setSubmitErrorMessages({});
            showErrorNotification(
                error instanceof Error
                    ? error.message
                    : 'Failed to submit survey',
            );
        }
    }, [
        itemStates,
        surveyItems,
        surveyId,
        hasChanges,
        saveProgress,
        submitDivisionalManagerSurveyMutation,
        onSuccess,
    ]);

    const getSubmitErrorMessage = useCallback(
        (itemId: string): string | undefined => submitErrorMessages[itemId],
        [submitErrorMessages],
    );

    return {
        itemStates,
        getItemState,
        updateItemState,
        markForPcr,
        markAsNA,
        setProposedPrice,
        setNaReason,
        clearItemState,
        submitErrorIds,
        getSubmitErrorMessage,
        allItemsActioned,
        completionPercent,
        hasChanges,
        changedCount,
        saveProgress,
        submitSurvey,
        isSaving,
        isSubmitting,
    };
}
